// Thin client for the /api/v1 backend. Session is an httpOnly cookie, so no token handling here.
export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

let onUnauthenticated: (() => void) | null = null;
export function setUnauthenticatedHandler(fn: () => void) {
  onUnauthenticated = fn;
}

type Query = Record<string, string | number | boolean | undefined | null>;

export function qs(query?: Query) {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  const s = params.toString();
  return s ? `?${s}` : '';
}

async function request<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const response = await fetch(`/api/v1${path}${qs(query)}`, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await response.text();
  const data = text ? safeJson(text) : null;
  if (!response.ok) {
    if (response.status === 401 && !path.startsWith('/auth/login')) onUnauthenticated?.();
    const err = (data ?? {}) as { error?: string; message?: string; details?: unknown };
    throw new ApiError(response.status, err.error ?? 'HTTP_' + response.status, err.message ?? `Request failed (${response.status}).`, err.details);
  }
  return data as T;
}

function safeJson(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, undefined, query),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

/** Opens a report as CSV download (same-origin GET, cookie sent automatically). */
export function downloadCsv(path: string, query: Query) {
  const a = document.createElement('a');
  a.href = `/api/v1${path}${qs({ ...query, format: 'csv' })}`;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
}

export function errorMessage(error: unknown) {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

/** Reads a picked file as base64 (no data: prefix) for JSON uploads. */
export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });
}

export const UPLOAD_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** A picked file as an upload body (PDF, PNG or JPEG up to 10 MB, base64). */
export async function uploadBody(file: File) {
  if (!UPLOAD_TYPES.includes(file.type)) throw new ApiError(400, 'BAD_FILE', 'Upload a PDF, PNG or JPEG file.');
  if (file.size > MAX_UPLOAD_BYTES) throw new ApiError(400, 'BAD_FILE', 'Files can be up to 10 MB.');
  return { filename: file.name, contentType: file.type, data: await fileToBase64(file) };
}

/** Uploads a quote or the signed PO attachment to a purchase request. */
export async function uploadPrFile<T>(prId: string, kind: 'quote' | 'signed_attachment', file: File): Promise<T> {
  return api.post<T>(`/purchase-requests/${prId}/files`, { kind, ...await uploadBody(file) });
}
